import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Linkedin, Facebook, ArrowUpRight, MessageCircle } from "lucide-react";
import { brand, contact, principals, social } from "../../data/mindscope";
import BrandLogo from "./BrandLogo";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#050914] text-white/80 pt-24 pb-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
           style={{
             backgroundImage:
               "radial-gradient(circle at 15% 20%, #C9A961 0%, transparent 40%), radial-gradient(circle at 85% 60%, #1A325B 0%, transparent 45%)",
           }} />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
        <div className="grid md:grid-cols-12 gap-10 md:gap-6">
          <div className="md:col-span-5">
            <Link to="/" className="inline-block mb-8" data-testid="footer-brand">
              <BrandLogo variant="transparent" className="h-16 md:h-20 w-auto" />
            </Link>
            <p className="font-devanagari text-ms-gold text-xl md:text-2xl leading-relaxed mb-3">
              {brand.taglineSanskrit}
            </p>
            <p className="font-display italic text-white/60 text-lg">
              {brand.taglineEnglish}
            </p>
            <div className="flex items-center gap-3 mt-8">
              {[
                { Icon: Linkedin, label: "LinkedIn", href: social.linkedin },
                { Icon: Facebook, label: "Facebook", href: social.facebook },
                { Icon: Instagram, label: "Instagram", href: social.instagram },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-ms-navy hover:bg-ms-gold hover:border-ms-gold transition-colors duration-500"
                >
                  <Icon size={16} strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="eyebrow mb-6">Navigate</h4>
            <ul className="space-y-3 font-body text-sm">
              {[
                ["Home", "/"],
                ["About", "/about"],
                ["Vision", "/vision"],
                ["Leadership", "/leadership"],
                ["Services", "/services"],
                ["Contact", "/contact"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link
                    data-testid={`footer-link-${label.toLowerCase()}`}
                    to={to}
                    className="text-white/70 hover:text-ms-gold transition-colors duration-300 inline-flex items-center gap-2 group"
                  >
                    <span className="w-0 h-px bg-ms-gold group-hover:w-4 transition-[width] duration-500" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="eyebrow mb-6">Office</h4>
            <div className="space-y-4 font-body text-sm text-white/70 leading-relaxed">
              <div className="flex items-start gap-3">
                <MapPin size={14} className="text-ms-gold mt-1 shrink-0" strokeWidth={1.6} />
                <div>
                  <div>{contact.address.line1}</div>
                  <div>{contact.address.line2}</div>
                  <div className="text-white/50">{contact.address.line3}</div>
                </div>
              </div>
              {contact.phones.map((p) => (
                <div key={p.tel} className="flex items-center gap-3">
                  <Phone size={14} className="text-ms-gold shrink-0" strokeWidth={1.6} />
                  <a href={`tel:${p.tel}`} className="hover:text-ms-gold transition-colors" data-testid={`footer-phone-${p.name.split(' ')[0].toLowerCase()}`}>
                    <span className="text-white/50 text-xs mr-2 uppercase tracking-wider">{p.name}</span>
                    {p.number}
                  </a>
                </div>
              ))}
              {contact.emails.map((e) => (
                <div key={e} className="flex items-center gap-3">
                  <Mail size={14} className="text-ms-gold shrink-0" strokeWidth={1.6} />
                  <a href={`mailto:${e}`} className="hover:text-ms-gold transition-colors" data-testid={`footer-email-${e.split('@')[0]}`}>
                    {e}
                  </a>
                </div>
              ))}

              {/* WhatsApp pills */}
              <div className="pt-2 flex flex-wrap gap-2">
                {principals.map((p) => (
                  <a
                    key={p.whatsapp}
                    href={p.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-whatsapp-${p.name.split(' ')[0].toLowerCase()}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#25D366]/40 text-[#25D366] text-[0.65rem] tracking-[0.22em] uppercase hover:bg-[#25D366] hover:text-ms-navy transition-colors"
                  >
                    <MessageCircle size={12} strokeWidth={2} />
                    {p.name.split(' ')[0]}
                  </a>
                ))}
              </div>

              <p className="text-white/50 text-xs tracking-[0.2em] uppercase mt-4">{contact.hours}</p>
            </div>
          </div>
        </div>

        <div className="hairline my-14 opacity-30" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs tracking-[0.2em] uppercase text-white/40">
          <p>© {new Date().getFullYear()} Mindscope Advisor LLP · All Rights Reserved</p>
        </div>
      </div>
    </footer>
  );
}
